/**
 * Mark Deal Won Handler
 * Edit handler for marking deals as won
 */

import type { ToolExecutionContext } from '../../types/index.js';
import type { EditPreview } from '../../types/index.js';
import { EditHandlerBase } from '../base/index.js';
import { uuidField } from '../../utils/schema-builder.js';
import { generateDiff } from '../../utils/diff-generator.js';
import { generateConfirmationToken } from '../../utils/confirmation-token.js';
import { createUserGraphQLClient } from '../../utils/graphql-client.js';
import { validateDealId } from '../../utils/deal-validation.js';

/**
 * Mark deal won handler
 * Marks a deal as won and sets the won_at timestamp
 */
export class MarkDealWonHandler extends EditHandlerBase<any> {
  id = 'mark_deal_won';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the deal to mark as won'),
      notes: {
        type: 'string',
        description: 'Optional notes about the win',
      },
    },
    required: ['id'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    // Validate input
    const id = validateDealId(params.id);
    const notes = params.notes;

    if (!context?.userToken) {
      throw new Error('User token is required for deal update');
    }

    try {
      const { GetDealDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Fetch current deal
      const result = await client.query({
        query: GetDealDocument,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const currentDeal = data?.dealsCollection?.edges?.[0]?.node;
      
      if (!currentDeal) {
        throw new Error(`Deal with ID "${id}" not found or you don't have permission to edit it`);
      }

      // Check if deal is already won or lost
      if (currentDeal.stage === 'won' || currentDeal.stage === 'lost') {
        throw new Error(`Cannot mark deal as won: deal is already in "${currentDeal.stage}" stage`);
      }

      // Build updates
      const updates: any = {
        stage: 'won',
        won_at: new Date().toISOString(),
      };

      if (notes) {
        updates.notes = (currentDeal.notes || '') + (currentDeal.notes ? '\n\n' : '') + `[Won] ${notes}`;
      }

      // Generate merged state
      const merged = { ...currentDeal, ...updates };
      const changes = generateDiff(currentDeal, merged);

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'deal',
        entityId: id,
        changes: updates,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'deal',
        id,
        changes,
        confirmationToken: token,
        preview: merged,
      };
    } catch (error) {
      throw new Error(`Mark deal won failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
